<template>
  <Layout title="Book a Ride" description="Create your booking with full details.">
    <div class="container py-5" style="max-width: 800px;">
      <div class="card shadow">
        <div class="card-header bg-dark text-light d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h3 class="mb-0">Book a Ride</h3>
            <small class="text-muted">Fill in the details below to book your ride</small>
          </div>

          <button
            type="button"
            class="btn btn-success d-flex align-items-center gap-2 fw-semibold"
            @click="$router.push('/Tutorial')"
          >
            Tutorial
          </button>
        </div>


        <div class="card-body">

          <form @submit.prevent="handleSubmit" class="row g-4">
            <!-- Start Date -->
            <div class="col-md-6">
              <label for="startDate" class="form-label">Start Date *</label>
              <input
                type="date"
                id="startDate"
                v-model="startDate"
                class="form-control"
                required
              />
            </div>

            <!-- Start Time -->
            <div class="col-md-6">
              <label for="startTime" class="form-label">Start Time *</label>
              <br/>
               <div class="w-100">
                <VueTimepicker
                  v-model="formData.startTime"
                  format="HH:mm"
                  :hour-range="Array.from({ length: 24 }, (_, i) => i)"
                  :minute-range="Array.from({ length: 60 }, (_, i) => i)"
                  manual-input
                  class="vue__time-picker w-100"
                />

              </div>

            </div>

            <!-- Vessel Name -->
            <div class="col-md-6">
              <label for="vesselName" class="form-label">Vessel Name (Optional)</label>
              <input
                type="text"
                id="vesselName"
                v-model="formData.vesselName"
                class="form-control"
                placeholder="Enter vessel name if applicable"
              />
            </div>

            <!-- Counter Dropdown -->
            <div class="col-md-6">
              <label for="counterNumber" class="form-label">Counter *</label>
              <select
                id="counterNumber"
                v-model="formData.counterNumber"
                class="form-select"
                required
              >
                <option disabled value="">Select Counter</option>
                <option value= "NA">N/A</option>
                <option v-for="n in 16" :key="n" :value="'Counter ' + n">
                  Counter {{ n }}
                </option>
              </select>
            </div>
            <!-- Client Dropdown -->
              <div class="col-md-6">
                <label for="clientSelect" class="form-label">Agent*</label>
                <select
                  id="clientSelect"
                  v-model="formData.clientId"
                  class="form-select"
                  required
                >
                  <option disabled value="">Select Client</option>
                  <option v-if="isAdmin" value="admin">
                    Admin
                  </option>

                  <option
                    v-for="client in clients"
                    :key="client.id"
                    :value="client.id"
                  >
                    {{ client["contactPerson"] }}
                  </option>
                </select>
              </div>
            <!-- Crew Name (Dynamic Array) -->
            <div class="col-md-6">
              <label for="vesselName" class="form-label">Crew Rank and Name</label>
              <input
                type="text"
                id="crewName"
                v-model="formData.crewName"
                class="form-control"
                placeholder="Enter Crew Name"
              />
            </div>
            <!-- Crew Contacts (Dynamic Array) -->
            <div class="col-md-12">
              <label class="form-label">
                Crew Contacts <small>(Include country code)</small> *
              </label>

              <div
                v-for="(contact, index) in formData.pocContact"
                :key="index"
                class="d-flex gap-2 mb-2 align-items-center"
              >
                <vue-tel-input
                  ref="pocTelInputRefs"
                  :ref_for="true"
                  v-model="formData.pocContact[index]"
                  mode="international"
                  placeholder="Enter contact number"
                  required
                  style="flex:1;"
                />



                <button
                  type="button"
                  class="btn btn-danger"
                  @click="removeContact(index)"
                  v-if="formData.pocContact.length > 1"
                >
                  Remove
                </button>
              </div>

              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="addContact"
              >
                + Add Contact
              </button>
            </div>

            


            <!-- Start Location -->
            <div class="col-12">
              <label for="startLocation" class="form-label">Start Location *</label>
              <input
                type="text"
                id="startLocation"
                v-model="formData.startLocation"
                class="form-control"
                placeholder="Enter pickup location"
                required
              />
            </div>

            <!-- Trips (Dynamic Array) -->
            <div class="col-12">
              <label class="form-label">Additional (Optional)</label>
              <div
                v-for="(trip, index) in formData.trips"
                :key="index"
                class="d-flex gap-2 mb-2"
              >
                <input
                  type="text"
                  class="form-control"
                  v-model="formData.trips[index]"
                  placeholder="e.g. Marina Bay → Changi Airport"
                />
                <button
                  type="button"
                  class="btn btn-danger"
                  @click="removeTrip(index)"
                >
                  Remove
                </button>
              </div>
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="addTrip"
              >
                + Add Trip
              </button>
            </div>


            <!-- End Location -->
            <div class="col-12">
              <label for="endLocation" class="form-label">End Location *</label>
              <input
                type="text"
                id="endLocation"
                v-model="formData.endLocation"
                class="form-control"
                placeholder="Enter drop-off location"
                required
              />
            </div>

            <!-- Flight Numbers (Dynamic Array) -->
            <div class="col-12">
              <label class="form-label">Flight Numbers</label>

              <div
                v-for="(flight, index) in formData.flightNumbers"
                :key="index"
                class="d-flex gap-3 mb-2 align-items-center flex-wrap"
              >
                <!-- Flight Number Input -->
                <input
                  type="text"
                  class="form-control"
                  v-model="flight.number"
                  placeholder="e.g. SQ316"
                  style="flex: 1;"
                />

                <!-- Arrival Checkbox -->
                <div class="form-check d-flex align-items-center">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="flight.isArrival"
                    :id="`arrival-${index}`"
                    @change="handleArrivalChange(index)"
                  />
                  <label class="form-check-label ms-1" :for="`arrival-${index}`">
                    Arrival
                  </label>
                </div>

                <!-- Departure Checkbox -->
                <div class="form-check d-flex align-items-center">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="flight.isDeparture"
                    :id="`departure-${index}`"
                    @change="handleDepartureChange(index)"
                  />
                  <label class="form-check-label ms-1" :for="`departure-${index}`">
                    Departure
                  </label>
                </div>


                <!-- Remove Button -->
                <button
                  type="button"
                  class="btn btn-danger"
                  @click="removeFlight(index)"
                >
                  Remove
                </button>
              </div>

              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="addFlight"
              >
                + Add Flight
              </button>
            </div>




            <!-- Number of People -->
            <div class="col-md-12">
              <label for="pax" class="form-label">Number of People *</label>
              <input
                type="number"
                id="pax"
                v-model="formData.pax"
                class="form-control"
                min="1"
                placeholder="1"
                required
              />
            </div>

            <!-- Number of Luggage -->
            <!-- <div class="col-md-6">
              <label for="luggage" class="form-label">Number of Luggage</label>
              <input
                type="number"
                id="luggage"
                v-model="formData.luggage"
                class="form-control"
                min="0"
                placeholder="0"
              />
            </div> -->

            <!-- File Upload (Dynamic like Flight) -->
            <div class="col-12">
              <label class="form-label">Upload Documents (Optional)</label>
              <div
                v-for="(fileObj, index) in uploadedFiles"
                :key="index"
                class="d-flex gap-2 mb-2"
              >
                <input
                  type="file"
                  class="form-control"
                  @change="onFileChange($event, index)"
                />
                <button
                  type="button"
                  class="btn btn-danger"
                  @click="removeFile(index)"
                >
                  Remove
                </button>
              </div>
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="addFile"
              >
                + Add File
              </button>
              <ul class="mt-2 small" v-if="uploadedFiles.length > 0">
                <li v-for="(fileObj, index) in uploadedFiles" :key="'file-' + index">
                  {{ fileObj.file ? fileObj.file.name : 'No file selected' }}
                </li>
              </ul>
            </div>

            <!-- Remarks -->
            <div class="col-12">
              <label for="remarks" class="form-label">Remarks</label>
              <textarea
                id="remarks"
                v-model="formData.remarks"
                class="form-control"
                rows="3"
                placeholder="Any special notes"
              ></textarea>
            </div>

            <!-- Submit -->
            <div class="col-12">
              <button
                type="submit"
                class="btn btn-warning w-100 fw-bold"
                :disabled="isSubmitting"
              >
                <span v-if="!isSubmitting">Submit Booking</span>
                <span
                  v-else
                  class="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import { useToast } from 'vue-toastification'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import router from '@/router'
import axios from 'axios'
import { VueTelInput } from 'vue3-tel-input'
import 'vue3-tel-input/dist/vue3-tel-input.css'
import VueTimepicker from 'vue3-timepicker'
import 'vue3-timepicker/dist/VueTimepicker.css'

export default {
  name: 'BookingForm',
  components: { Layout, VueTelInput,VueTimepicker  },
  data() {
    return {
      startDate: '',
      isSubmitting: false,
      isAdmin: false, // 👈 new
      // $apiURL: 'http://127.0.0.1:5001',
      // $apiURL: 'https://amdtransportation-be-main-build-159134604691.us-central1.run.app',
      clients: [], // 👈 new
      uploadedFiles: [{ file: null }],
      formData: {
        clientId: "",
        startTime: '',
        startLocation: '',
        endLocation: '',
        vesselName: '',
        counterNumber: '',
        flightNumbers: [ { number: '', isArrival: false }],
        pax: '',
        luggage: '',
        remarks: '',
        pocContact: [''],
        trips: [''],
        crewName:""
      },
    }
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  async mounted() {
    this.userID = this.$store.getters.getUserId
    console.log(this.$store.state.systemAccess.admin)
    await this.checkAuthState()
    await this.loadClients()
  },
  methods: {
    async loadClients() {
      try {
        const headers = await this.getAuthHeader();
        const res = await axios.get(`${this.$apiURL}/clients`, { headers });
        this.clients = res.data.clients || [];
        console.log(this.clients)
      } catch (err) {
        console.error("❌ Failed to load clients:", err.message);
        this.toast.error("Failed to load clients");
      }
    },
    addTrip() {
      this.formData.trips.push('')
    },
    removeTrip(index) {
      this.formData.trips.splice(index, 1)
    },
    // Flight logic
    addFlight() {
      this.formData.flightNumbers.push({ number: '', isArrival: false, isDeparture: false })
    },
    removeFlight(index) {
      this.formData.flightNumbers.splice(index, 1)
    },
    handleArrivalChange(index) {
      if (this.formData.flightNumbers[index].isArrival) {
        this.formData.flightNumbers[index].isDeparture = false
      }
    },

    handleDepartureChange(index) {
      if (this.formData.flightNumbers[index].isDeparture) {
        this.formData.flightNumbers[index].isArrival = false
      }
    },

    // File upload logic
    addFile() {
      this.uploadedFiles.push({ file: null })
    },
    removeFile(index) {
      this.uploadedFiles.splice(index, 1)
    },
    addContact() {
      if (!Array.isArray(this.formData.pocContact)) {
        this.formData.pocContact = []
      }
      this.formData.pocContact.push('')
    },
    removeContact(index) {
      this.formData.pocContact.splice(index, 1)
    },
    onFileChange(e, index) {
      const file = e.target.files[0]
      if (file) this.uploadedFiles[index].file = file
    },

    async handleSubmit() {
      // if (
      //   !this.startDate ||
      //   !this.formData.startTime ||
      //   !this.formData.startLocation ||
      //   !this.formData.endLocation ||
      //   !this.formData.pax ||
      //   !this.formData.counterNumber
      // ) {
      //   this.toast.error('⚠️ Please fill in all required fields')
      //   return
      // }

      try {
        this.isSubmitting = true
        const headers = await this.getAuthHeader()

        // let rawPhoneNumber = this.$refs.pocTelInputRef ? this.$refs.pocTelInputRef.modelValue || this.$refs.pocTelInputRef.number : this.formData.pocContact 
        // if (!rawPhoneNumber && this.$refs.pocTelInputRef) { 
          
        //   rawPhoneNumber = this.$refs.pocTelInputRef.phone || 
        //                   this.$refs.pocTelInputRef.modelValue 
        //                   || this.formData.pocContact 
        //   }
        //  const finalPhoneNumber = rawPhoneNumber || this.formData.pocContact 
        //  if (!finalPhoneNumber)
        //  { console.log("HERE") 
        //  this.toast.error('⚠️ Please fill in all required fields') 
        //  }

        // ✅ Extract clean phone numbers from vue-tel-input refs
        let pocNumbers = []
        if (this.$refs.pocTelInputRefs && Array.isArray(this.$refs.pocTelInputRefs)) {
          pocNumbers = this.$refs.pocTelInputRefs
            .map(ref => ref?.modelValue || ref?.number || ref?.phone)
            .filter(v => !!v && v.trim() !== '')
        }

        // fallback if no refs detected
        if (!pocNumbers.length && this.formData.pocContact?.length) {
          pocNumbers = this.formData.pocContact.filter(v => !!v && v.trim() !== '')
        }



        const startTimeISO = new Date(
          `${this.startDate}T${this.formData.startTime}`
        ).toISOString()
        
        const payload = {
          clientId: this.formData.clientId, // 👈 added
          pax: Number(this.formData.pax),
          luggage: Number(this.formData.luggage || 0),
          startLocation: this.formData.startLocation,
          endLocation: this.formData.endLocation,
          startTime: startTimeISO,
          pocContact: pocNumbers,
          vesselName: this.formData.vesselName || null,
          crewName: this.formData.crewName ||"",
          counterNumber: this.formData.counterNumber,
          flightNumbers: this.formData.flightNumbers.filter(f =>  (f.number || '').trim() !== ''),
          remarks: this.formData.remarks,
          trips: this.formData.trips.filter((t) => t.trim() !== ''), // ✅ new line
        }

        const hasFiles = this.uploadedFiles.some((f) => f.file)
        let response

        if (hasFiles) {
          const formData = new FormData()
          for (const [key, value] of Object.entries(payload)) {
            if (Array.isArray(value)) formData.append(key, JSON.stringify(value))
            else if (value !== null && value !== undefined) formData.append(key, value)
          }
          this.uploadedFiles.forEach((f) => {
            if (f.file) formData.append('files', f.file)
          })
          response = await axios.post(`${this.$apiURL}/api/bookings`, formData, { headers })
        } else {
          response = await axios.post(`${this.$apiURL}/api/bookings`, payload, { headers })
        }

        if (response.data.ok) {
          this.toast.success('✅ Booking created successfully!')
          console.log('Booking created:', response.data)
          window.location.reload()
          // this.resetForm()
        } else {
          this.toast.error(response.data.error || 'Failed to create booking')
        }
      } catch (err) {
        console.error('❌ Booking API error:', err)
        this.toast.error(err.response?.data?.error || 'Error creating booking')
      } finally {
        this.isSubmitting = false
         if (this.$refs.pocTelInputRef) {
            try {
              this.$refs.pocTelInputRef.modelValue = ''
              this.$refs.pocTelInputRef.number = ''
              this.$refs.pocTelInputRef.phone = ''
            } catch (e) {
              console.warn('⚠️ Unable to fully reset vue-tel-input:', e)
            }
          }

          // ✅ clear uploaded file inputs
          this.uploadedFiles = [{ file: null }]

          // ✅ reset the main form data
          this.resetForm()
      }
    },

    resetForm() {
      this.startDate = ''
      this.uploadedFiles = [{ file: null }]
      this.formData = {
        startTime: '',
        startLocation: '',
        endLocation: '',
        vesselName: '',
        counterNumber: '',
        flightNumbers: [''],
        pax: '',
        luggage: '',
        remarks: '',
        pocContact: [''],
      }
    },

    async getAuthHeader() {
      const user = getAuth().currentUser
      if (!user) throw new Error('Not logged in')
      const token = await user.getIdToken()
      return { Authorization: token }
    },

    async checkAuthState() {
      const auth = getAuth()
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            try {
              const idToken = await user.getIdToken()
              this.token = idToken

              // ✅ Check claims to see if admin
              const res = await axios.get(`${this.$apiURL}/api/systemAccess`, {
                headers: { Authorization: idToken }
              })
              const claims = res.data.claims || {}
              this.isAdmin = !!claims.admin

              resolve()
            } catch (error) {
              reject(error)
            }
          } else {
            router.push('/')
            reject(new Error('No user logged in'))
          }
        })
      })
    },
  },
}
</script>

<style scoped>
button.btn-outline-primary.btn-sm {
  margin-top: 0.3rem;
}

.vue-timepicker-wrapper {
  width: 100%;
}

/* make its internal input match Bootstrap form-control */
:deep(.vue__time-picker input) {
  display: block;
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

:deep(.vue__time-picker input:focus) {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13,110,253,0.25);
}


</style>
